import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import { AnimatePresence, m } from 'framer-motion'
import { Check, ChevronLeft, ChevronRight, Pause } from 'lucide-react'
import { buildSelectionsFromAnswers } from '../gate-answers'
import { DURATION, EASE_OUT } from '../motion'
import { useVibeAgentStore } from '../store'
import type { GateQuestion, TranscriptEntry } from '../wire'
import { QuestionOption } from './question-option'
import { resolveGateCountdown, useGateCountdown } from './use-gate-countdown'
import { useQuestionNavigation } from './use-question-navigation'
import { useVibeAgentApi } from './use-vibe-agent-api'

// Crossfades the active question (mode="wait" in the parent AnimatePresence)
// and staggers its option rows in; each row's spring lives in question-option.
const questionSwap = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: DURATION.enter, ease: EASE_OUT, staggerChildren: 0.05 },
  },
  exit: {
    opacity: 0,
    transition: { duration: DURATION.exitFast, ease: EASE_OUT },
  },
}

const AnsweredQuestionDisplay = ({
  questions,
  selections,
}: {
  questions: GateQuestion[]
  selections: Record<number, string[]>
}) => (
  <div className="space-y-3">
    {questions.map((q, idx) => (
      <div key={q.header} className="space-y-1">
        <p className="text-p-400 text-primary">{q.question}</p>
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-tertiary flex-shrink-0" />
          <span className="text-p-400 text-secondary">
            {(selections[idx] || []).join(', ') || 'Answered in chat'}
          </span>
        </div>
      </div>
    ))}
  </div>
)

export const VibeQuestionCard = ({ entry }: { entry: TranscriptEntry }) => {
  const {
    questionData,
    questions,
    isAnswered,
    activeTab,
    goToQuestion,
    focusedOption,
    setFocusedOption,
    selections,
    customInputs,
    countdown,
    countdownState,
    canProceed,
    allQuestionsAnswered,
    handleOptionSelect,
    handleCustomInputChange,
    handleKeyDown,
    handleSubmit,
  } = useQuestionNavigation(entry)

  // Card-level auto-answer countdown: the per-option badge only renders when a
  // recommended option exists, leaving armed gates with NO visible timer. The
  // gate in the store is only this card's while it is the open one.
  const gate = useVibeAgentStore((state) => state.gate)
  const { holdGateTimer } = useVibeAgentApi()
  const isOwnGate = !!gate && gate.interactionId === entry.question_data?.interaction_id
  const { armed, seconds } = resolveGateCountdown(isOwnGate ? gate.autoAnswerSeconds : null)
  const remaining = useGateCountdown(armed, seconds)

  if (isAnswered) {
    const displaySelections = buildSelectionsFromAnswers(
      questions,
      questionData?.selected_answers ?? [],
    )
    return (
      <m.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: DURATION.exit, ease: EASE_OUT }}
        className="my-2 p-4 bg-bg2 border border-st2 rounded-2xl"
      >
        <AnsweredQuestionDisplay questions={questions} selections={displaySelections} />
      </m.div>
    )
  }

  const currentQuestion = questions[activeTab]
  if (!currentQuestion) return null

  return (
    <div
      role="group"
      aria-label="Vibe Agent question"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className="my-2 p-3 bg-bg2 border border-st2 rounded-2xl focus-visible:ring-2 focus-visible:ring-primary focus:outline-none"
      data-testid="container-question-renderer"
    >
      <div className="flex items-center justify-between gap-2 mb-4">
        <span className="text-p-500 text-primary">Vibe Agent needs your input</span>
        {armed && remaining > 0 && (
          <span className="relative overflow-hidden flex items-center gap-1 text-xs-500 bg-warning text-warning-foreground rounded-md pl-2.5 pr-1 py-0.5 whitespace-nowrap">
            {/* Codex-style draining fill: the darker layer shrinks toward zero
                as the auto-answer deadline approaches. Linear 1s transition
                rides each tick so the drain reads continuous. */}
            <span
              aria-hidden="true"
              className="absolute inset-0 origin-left bg-warning-foreground/10"
              style={{
                transform: `scaleX(${Math.min(Math.max(remaining / seconds, 0), 1)})`,
                transition: 'transform 1s linear',
              }}
            />
            <span className="relative">
              Auto-answering in <span className="font-jetbrains tabular-nums">{remaining}s</span>
            </span>
            <button
              type="button"
              onClick={holdGateTimer}
              className="relative p-0.5 rounded-full hover:bg-warning-foreground/15 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-warning-foreground/40"
              aria-label="Stop the auto-answer timer"
              data-testid="button-hold-gate-timer"
            >
              <Pause className="w-3 h-3" />
            </button>
          </span>
        )}
      </div>

      {questions.length > 1 && (
        <div className="flex items-center gap-1.5 mb-3">
          <Button
            variant="ghost"
            size="iconXs"
            onClick={() => goToQuestion(activeTab - 1)}
            disabled={activeTab === 0}
            aria-label="Previous question"
            data-testid="button-question-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs-500 text-secondary tabular-nums whitespace-nowrap">
            {activeTab + 1} of {questions.length}
          </span>
          <Button
            variant="ghost"
            size="iconXs"
            onClick={() => goToQuestion(activeTab + 1)}
            disabled={activeTab === questions.length - 1}
            aria-label="Next question"
            data-testid="button-question-next"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <span className="text-xs-500 text-tertiary truncate">{currentQuestion.header}</span>
          {canProceed(activeTab) && (
            <Check className="w-3.5 h-3.5 text-secondary ml-auto shrink-0" aria-hidden="true" />
          )}
        </div>
      )}

      <AnimatePresence mode="wait">
        <m.div
          key={activeTab}
          variants={questionSwap}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <p className="text-p-400 text-primary mb-4">{currentQuestion.question}</p>

          <div className="space-y-2 mb-4">
            {currentQuestion.options.map((opt, optIdx) => (
              <QuestionOption
                key={opt.label}
                option={opt}
                index={optIdx}
                isSelected={!!selections[activeTab]?.includes(opt.label)}
                isFocused={focusedOption === optIdx}
                countdownArmed={countdownState.armed}
                countdown={countdown}
                onSelect={() => handleOptionSelect(activeTab, opt.label)}
                onFocus={() => setFocusedOption(optIdx)}
              />
            ))}
          </div>

          <textarea
            rows={2}
            aria-label="Type your own answer"
            placeholder="Or type your own answer…"
            value={customInputs[activeTab] || ''}
            onChange={(e) => handleCustomInputChange(activeTab, e.target.value)}
            className={cn(
              'w-full mb-4 p-2.5 rounded-lg border border-st2 bg-bg1 resize-none',
              'text-p-400 text-primary placeholder:text-tertiary',
              'transition-[border-color] duration-150 focus:outline-none focus:border-primary/50',
            )}
            data-testid="input-question-custom"
          />
        </m.div>
      </AnimatePresence>

      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={!allQuestionsAnswered}
          disabledReason={!allQuestionsAnswered ? 'Answer all questions to submit' : undefined}
          data-testid="button-question-submit"
        >
          Submit
        </Button>
      </div>
    </div>
  )
}
